/**
 * core/store/appStore.ts — Zustand global store for Chanelle OS.
 *
 * Persists layout and board-ID preferences to localStorage via the
 * `persist` middleware so the user's workspace survives a page reload.
 * monday Storage is used for cross-device persistence (see useSettings).
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type {
  AppStore,
  AdminView,
  AppTab,
  BoardIdMap,
  MondayContext,
  SearchState,
  TabId,
  UserSettings,
  WidgetGridItem,
} from '@core/types';
import { DEFAULT_TAB_LAYOUTS } from '@utils/defaults';

// ─── Store ────────────────────────────────────────────────────────────────────

export const useAppStore = create<AppStore>()(
  persist(
    (set) => ({
      // ── Active tab ──────────────────────────────────────────────────────────
      activeTab: 'home' as TabId,
      setActiveTab: (tab: TabId) => set({ activeTab: tab }),

      // ── Edit mode ───────────────────────────────────────────────────────────
      isEditMode: false,
      setEditMode: (v: boolean) => set({ isEditMode: v }),

      // ── Command palette ─────────────────────────────────────────────────────
      commandPaletteOpen: false,
      setCommandPaletteOpen: (v: boolean) => set({ commandPaletteOpen: v }),

      // ── Monday context ──────────────────────────────────────────────────────
      context: null as MondayContext | null,
      setContext: (ctx: MondayContext) => set({ context: ctx }),

      // ── User settings ─────────────────────────────────────────────────────
      settings: null as UserSettings | null,
      setSettings: (s: UserSettings) => set({ settings: s }),

      // ── Config panel ──────────────────────────────────────────────────────
      configPanelInstanceId: null as string | null,
      configPanelWidgetId: null as string | null,
      setConfigPanel: (instanceId: string | null, widgetId: string | null) =>
        set({ configPanelInstanceId: instanceId, configPanelWidgetId: widgetId }),

      // ── Board IDs ───────────────────────────────────────────────────────────
      // Populated from monday Storage once settings are loaded.
      boardIds: {} as BoardIdMap,
      setBoardIds: (ids: BoardIdMap) => set({ boardIds: ids }),

      // ── Tab layouts ─────────────────────────────────────────────────────────
      tabLayouts: { ...DEFAULT_TAB_LAYOUTS },

      setTabLayout: (tab: TabId, layout: WidgetGridItem[]) =>
        set((state) => ({
          tabLayouts: { ...state.tabLayouts, [tab]: layout },
        })),

      resetTabLayout: (tab: TabId) =>
        set((state) => ({
          tabLayouts: {
            ...state.tabLayouts,
            [tab]: DEFAULT_TAB_LAYOUTS[tab] ?? [],
          },
        })),

      resetAllLayouts: () =>
        set({ tabLayouts: { ...DEFAULT_TAB_LAYOUTS } }),

      saveSnapshot: (_tab: AppTab) => {
        // Layout is persisted via React Query + monday Storage on every change.
        // Extend to save named snapshots if needed.
      },

      resetPageLayout: (tab: AppTab) =>
        set((state) => ({
          tabLayouts: {
            ...state.tabLayouts,
            [tab.id]: DEFAULT_TAB_LAYOUTS[tab.id] ?? [],
          },
        })),

      // ── Search ──────────────────────────────────────────────────────────────
      searchState: {
        query: '',
        results: [],
        isSearching: false,
      } satisfies SearchState,

      setSearchState: (patch: Partial<SearchState>) =>
        set((state) => ({ searchState: { ...state.searchState, ...patch } })),

      clearSearch: () =>
        set({
          searchState: { query: '', results: [], isSearching: false },
        }),

      // ── Admin panel ─────────────────────────────────────────────────────────
      adminView: 'status' as AdminView,
      setAdminView: (view: AdminView) => set({ adminView: view }),
    }),
    {
      name: 'chanelle-os-store',
      version: 2,
      storage: createJSONStorage(() => localStorage),
      // Only persist layout and boardIds — ephemeral UI state is not persisted.
      partialize: (state) => ({
        tabLayouts: state.tabLayouts,
        boardIds: state.boardIds,
        activeTab: state.activeTab,
      }),
      migrate: (persisted: unknown, version: number) => {
        const state = persisted as Record<string, unknown>;

        // Helper: replace mini_calendar → google_calendar in any layout array
        const migrateWidgetIds = (items: WidgetGridItem[]): WidgetGridItem[] =>
          items.map((item) =>
            item.widgetId === 'mini_calendar'
              ? { ...item, widgetId: 'google_calendar', instanceId: item.instanceId.replace('mini_calendar', 'google_calendar') }
              : item,
          );

        if (version < 2) {
          // v0/v1→v2: Replace mini_calendar widget with google_calendar in all stored layouts
          const layouts = state.tabLayouts as Record<string, WidgetGridItem[]> | undefined;
          if (layouts) {
            for (const tabId of Object.keys(layouts)) {
              if (Array.isArray(layouts[tabId])) {
                layouts[tabId] = migrateWidgetIds(layouts[tabId]);
              }
            }
          }
        }
        return state as unknown as AppStore;
      },
    },
  ),
);

// ─── Selectors ────────────────────────────────────────────────────────────────

/** Derived: true when all 5 board IDs have been configured */
export const selectBoardsConfigured = (state: AppStore): boolean => {
  const { boardIds } = state;
  return Boolean(
    boardIds.topics &&
      boardIds.notes &&
      boardIds.actions &&
      boardIds.meetings &&
      boardIds.settings,
  );
};
